// src/features/dashboard/components/DashboardHome.js
import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../../utils/formatUtils';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  PieChart, 
  FilePlus, 
  Receipt, 
  FileText,
  Users, 
  ArrowUpRight, 
  Download,
  Filter,
  TrendingUp,
  Wallet,
  CreditCard,
  ChevronRight,
  Calendar,
  Clock
} from 'lucide-react';
import useApi from '../../../hooks/useApi';
import { fetchDashboardSummary, fetchRecentCollaborators, fetchMyActivities, fetchReportActivities } from '../../../services/dashboardService';

import RecentReportsTable from '../components/RecentReportsTable';
import MyActivitiesTable from '../components/MyActivitiesTable';
import ReportActivitiesTable from '../components/ReportActivitiesTable';
import RecordsDonutChart from '../components/RecordsDonutChart';

function getInitials(name) {
  if (!name) return '';
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const DashboardHome = () => {
  const { data: summary, loading: summaryLoading, error: summaryError, callApi: fetchSummary } = useApi(fetchDashboardSummary, {showSuccessToast: false});
  const { data: collaborators, loading: collaboratorsLoading, error: collaboratorsError, callApi: fetchCollaboratorsData } = useApi(fetchRecentCollaborators, {showSuccessToast: false});
  const navigate = useNavigate();

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchCollaboratorsData(); }, [fetchCollaboratorsData]);
  
  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Financial Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's an overview of your finances.</p> 
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center">
          <div className="h-12 w-12 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center mr-4">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">{summaryLoading ? '...' : formatCurrency(summary?.total_revenue || 0)}</p>
            <div className="flex items-center mt-1">
              <span className="text-xs font-medium text-gray-600">
                {summaryLoading ? '' : `${summary?.revenue_count || 0} reports`}
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center">
          <div className="h-12 w-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mr-4">
            <CreditCard className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Expenses</p>
            <p className="text-2xl font-bold text-gray-900">{summaryLoading ? '...' : formatCurrency(summary?.total_expenses || 0)}</p>
            <div className="flex items-center mt-1">
              <span className="text-xs font-medium text-gray-600">
                {summaryLoading ? '' : `${summary?.expenses_count || 0} reports`}
              </span>
            </div>
          </div>
        </div> 
        
        {/* Outstanding Proformas card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center">
          <div className="h-12 w-12 rounded-lg bg-yellow-100 text-yellow-600 flex items-center justify-center mr-4">
            <FilePlus className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Outstanding Proformas</p>
            <p className="text-2xl font-bold text-gray-900">{summaryLoading ? '...' : summary?.outstanding_proformas_count || 0}</p>
            <div className="flex items-center mt-1">
              <span className="text-xs font-medium text-gray-600">
                {summaryLoading ? '' : `Total: ${formatCurrency(summary?.outstanding_proformas_amount || 0)}`}
              </span>
            </div>
          </div>
        </div>
        
        {/* Open Bills card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center">
          <div className="h-12 w-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center mr-4">
            <FileText className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Open Bills</p>
            <p className="text-2xl font-bold text-gray-900">{summaryLoading ? '...' : summary?.open_bills_count || 0}</p>
            <div className="flex items-center mt-1">
              <span className="text-xs font-medium text-gray-600">
                {summaryLoading ? '' : `Total: ${formatCurrency(summary?.open_bills_amount || 0)}`}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Reports & Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent Reports Table - Moved up in priority */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Recent Reports</h2>
                <button className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm flex items-center hover:bg-gray-50">
                  <FilePlus className="h-4 w-4 mr-1.5" />
                  <span>Add Report</span>
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <RecentReportsTable />
            </div>
          </div>
          
          {/* Chart Panel */}
          <RecordsDonutChart />

          {/* Recent Activities on My Reports */}
          <ReportActivitiesTable />
        </div>
        
        {/* Right Column - Collaborators & Activity */}
        <div className="space-y-6">
          {/* Collaborator Access */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Collaborators</h2>
                <Link to="collaborators/sent" className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center">
                  Manage
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
            <div className="p-4">
              {collaboratorsLoading ? (
                <div className="text-center text-gray-500 py-4">Loading collaborators...</div>
              ) : collaboratorsError ? (
                <div className="text-center text-red-500 py-4">{collaboratorsError}</div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {collaborators && collaborators.map(inv => {
                    const name =  inv.recipient_details?.username || '';
                    const initials = getInitials(name);
                    let colorClass = 'bg-gray-600';
                    if (inv.permission_level === 'full_management') colorClass = 'bg-primary-600';
                    else if (inv.permission_level === 'view_download') colorClass = 'bg-secondary-600';
                    return (
                      <li key={inv.id} className="py-3 flex items-center">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3 ${colorClass}`}>
                          {initials}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{name}</p>
                          <p className="text-xs text-gray-500">{inv.permission_level === 'full_management' ? 'Full Access' : inv.permission_level === 'view_download' ? 'View & Download' : 'View Only'}</p>
                        </div>
                      </li>
                    );
                  })}
                  {(!collaborators || collaborators.length === 0) && (
                    <li className="py-3 text-center text-gray-400">No collaborators yet.</li>
                  )}
                </ul>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center" onClick={()=>navigate('/dashboard/collaborators')}>
                <Users className="h-4 w-4 mr-1.5" />
                <span>Invite Collaborator</span>
              </button>
            </div>
          </div>
          
          {/* My Recent Activities */}
          <MyActivitiesTable />

        </div>
      </div>
    </>
  );
};




export default DashboardHome;